import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';

import {
  CollaborationToolType,
  useCollaborationToolOptionsIntegrationsQuery,
  useCollaborationToolOptionsQuery,
} from 'src/generated/mloop-graphql';

import { useOrgCollaborationService } from 'src/hooks/collaborationService';

export const NO_CODING_LINK_OPTION_ID = 'NO_CODING_LINK';
export const BLANK_CODING_OPTION_ID = 'BLANK';

export const NO_CODING_LINK_OPTION = {
  name: 'No coding link',
  id: NO_CODING_LINK_OPTION_ID,
  groupName: null,
};

const useCollaborationToolOptions = () => {
  const { t } = useTranslation();
  const collaborationToolService = useOrgCollaborationService();
  const { data: integrations } = useCollaborationToolOptionsIntegrationsQuery();

  const collaborationToolType = useMemo(() => {
    if (integrations?.thisOrg?.integrations?.coderpad) {
      return CollaborationToolType.Coderpad;
    }
    if (integrations?.thisOrg?.integrations?.codility) {
      return CollaborationToolType.Codility;
    }
    if (integrations?.thisOrg?.integrations?.codesignal) {
      return CollaborationToolType.Codesignal;
    }
    if (integrations?.thisOrg?.integrations?.hackerrank) {
      return CollaborationToolType.Hackerrank;
    }

    return undefined;
  }, [integrations]);

  const BLANK_CODING_TEMPLATE = useMemo(() => {
    return {
      name: t('hiring-dashboard-common:common.select.noTemplate', { tool: collaborationToolService?.name }),
      id: BLANK_CODING_OPTION_ID,
      groupName: null,
    };
  }, [collaborationToolService?.name, t]);

  const { data, loading } = useCollaborationToolOptionsQuery({
    variables: {
      input: {
        // This is just to make typescript happy this will not be used in the query
        collaborationToolType: collaborationToolType || CollaborationToolType.Coderpad,
        pageInput: {
          limit: 100,
        },
      },
    },
    skip: !collaborationToolType,
  });

  const options = useMemo(() => {
    if (!data?.collaborationLinkTemplates.items.length) {
      return [];
    }

    const mappedOptions = data?.collaborationLinkTemplates.items.map((each) => {
      return {
        name: each.name,
        id: each.id,
        groupName: each.groupName || t('hiring-dashboard-common:common.select.otherTemplates'),
      };
    });

    const opt = [BLANK_CODING_TEMPLATE, ...(mappedOptions ?? [])].sort((a, b) => {
      if (b.groupName === a.groupName) {
        return 0;
      }
      return (a.groupName || '') < (b.groupName || '') ? -1 : 1;
    });
    return opt;
  }, [BLANK_CODING_TEMPLATE, data?.collaborationLinkTemplates.items, t]);

  return { options, loading };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CollaborationToolSelectOrgQuery = gql`
  query CollaborationToolOptionsIntegrations {
    thisOrg {
      integrations {
        id
        coderpad {
          apiKey
        }
        codility {
          apiKey
        }
        codesignal {
          apiKey
        }
        hackerrank {
          apiKey
        }
      }
      id
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CodelinkTemplateSelectQuery = gql`
  query CollaborationToolOptions($input: CollaborationLinkTemplatesInput!) {
    collaborationLinkTemplates(input: $input) {
      items {
        id
        groupName
        name
      }
      nextCursor
      __typename
    }
  }
`;

export default useCollaborationToolOptions;
